/*
 * Copyright © 2021-present Protonbase Inc. All rights reserved.
 * The information contained in this software is confidential and proprietary to Protonbase Inc. and its subsidiaries. No part of this software may be used, copied, reproduced, modified, displayed, performed, executed, published, distributed, conveyed, stored, transmitted, or otherwise exploited in any form or by any means, including but not limited to electronic, mechanical, photocopying, recording, transcribing, without the prior written consent of Protonbase Inc.
 * Any violation of the above terms will constitute an infringement of copyright and will be subjected to legal action.
 */

import { accountClient } from '@/services/cloudClient/accountClient'
import { DownloadOutlined, FileTextOutlined } from '@ant-design/icons'
import type { ModelPrivateOfferAttachment } from 'account-client'
import { App, Button, Card, List, Space, Typography } from 'antd'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

const { Text, Title } = Typography

interface AttachmentListProps {
    contractId: string
    offerId: string
}

export function AttachmentList({ contractId, offerId }: AttachmentListProps) {
    const { t } = useTranslation()
    const { message } = App.useApp()
    const [attachments, setAttachments] = useState<ModelPrivateOfferAttachment[]>([])
    const [loading, setLoading] = useState(false)
    const [downloadingIds, setDownloadingIds] = useState<Set<string>>(new Set())

    // 加载附件列表
    const loadAttachments = async () => {
        if (!offerId || !contractId) return

        setLoading(true)
        try {
            const response = await accountClient.privateOfferAttachmentsApi.apiV1ContractsIdPrivateOffersPoidAttachmentsGet(
                contractId,
                offerId
            )
            setAttachments(response.data || [])
        } catch (error) {
            console.error('Failed to load attachments:', error)
            // 不显示错误，因为可能是权限问题或者没有附件
        } finally {
            setLoading(false)
        }
    }

    // 初始加载
    useEffect(() => {
        loadAttachments()
    }, [offerId, contractId])

    // 下载附件
    const handleDownload = async (attachment: ModelPrivateOfferAttachment) => {
        if (!attachment.id) return

        setDownloadingIds(prev => new Set(prev).add(attachment.id!))

        try {
            const response = await accountClient.privateOfferAttachmentsApi.apiV1ContractsIdPrivateOffersPoidAttachmentsAidDownloadGet(
                contractId,
                offerId,
                attachment.id,
                { responseType: 'blob' }
            )

            // 创建下载链接
            const url = window.URL.createObjectURL(new Blob([response.data]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', attachment.originalFileName || `attachment-${attachment.id}.pdf`)
            document.body.appendChild(link)
            link.click()
            link.remove()
            window.URL.revokeObjectURL(url)

            message.success(t('Download started'))
        } catch (error: any) {
            console.error('Download error:', error)
            const errorMessage = error?.response?.data?.message || error?.message || t('Download failed')
            message.error(`${t('Failed to download attachment')}: ${errorMessage}`)
        } finally {
            setDownloadingIds(prev => {
                const newSet = new Set(prev)
                newSet.delete(attachment.id!)
                return newSet
            })
        }
    }

    // 格式化文件大小
    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes'
        const k = 1024
        const sizes = ['Bytes', 'KB', 'MB', 'GB']
        const i = Math.floor(Math.log(bytes) / Math.log(k))
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    }

    // 格式化上传时间
    const formatUploadTime = (createdAt: string) => {
        if (!createdAt) return ''
        try {
            return new Date(createdAt).toLocaleDateString()
        } catch (error) {
            return ''
        }
    }

    // 如果没有附件且不在加载中，不显示组件
    if (!loading && attachments.length === 0) {
        return null
    }

    return (
        <Card>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                <div className="section-indicator" style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    marginRight: 8
                }} />
                <Title level={4} className="section-title" style={{ margin: 0 }}>
                    {t('Legal Terms & Conditions')}
                </Title>
            </div>
            <div style={{ marginBottom: 12, marginLeft: 16 }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                    {t('Legal terms and conditions documents')}
                </Text>
            </div>
            <div className="attachment-card">
                {attachments.length > 0 ? (
                    <>
                        <List
                            itemLayout="horizontal"
                            dataSource={attachments}
                            style={{ paddingLeft: 24, paddingRight: 24 }}
                            renderItem={(attachment) => (
                                <List.Item
                                    actions={[
                                        <Button
                                            key="download"
                                            type="primary"
                                            size="small"
                                            icon={<DownloadOutlined />}
                                            loading={downloadingIds.has(attachment.id!)}
                                            onClick={() => handleDownload(attachment)}
                                            style={{
                                                fontSize: '12px',
                                                height: '28px',
                                                borderRadius: '4px'
                                            }}
                                        >
                                            {t('Download')}
                                        </Button>
                                    ]}
                                >
                                    <List.Item.Meta
                                        avatar={
                                            <FileTextOutlined
                                                style={{
                                                    fontSize: 20,
                                                    color: '#1890ff',
                                                    marginTop: 4
                                                }}
                                            />
                                        }
                                        title={
                                            <div>
                                                <Text strong style={{ fontSize: '14px', color: '#262626' }}>
                                                    {attachment.originalFileName}
                                                </Text>
                                            </div>
                                        }
                                        description={
                                            <Space size={0} split={<span style={{ color: '#d9d9d9', margin: '0 4px' }}>•</span>}>
                                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                                    {formatFileSize(attachment.fileSize || 0)}
                                                </Text>
                                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                                    PDF
                                                </Text>
                                                {attachment.createdAt && (
                                                    <Text type="secondary" style={{ fontSize: '12px' }}>
                                                        {formatUploadTime(attachment.createdAt)}
                                                    </Text>
                                                )}
                                            </Space>
                                        }
                                    />
                                </List.Item>
                            )}
                        />
                    </>
                ) : (
                    <div style={{
                        textAlign: 'center',
                        padding: '20px 0',
                        color: 'var(--private-offer-text-secondary)'
                    }}>
                        <FileTextOutlined style={{ fontSize: 24, marginBottom: 8, color: 'var(--private-offer-text-secondary)' }} />
                        <div>
                            <Text type="secondary">{t('No attachments available')}</Text>
                        </div>
                    </div>
                )}
            </div>
        </Card>
    )
}
